import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import Icon from './Icon'
import Popover from './Popover'

/**
 * Custom select (design system) — a fully styled, accessible replacement for the
 * native `<select>` on the authenticated surfaces (operator / portal). Trigger +
 * popover listbox, themed to the FreightOS mockups (mockups/operator/app.css
 * `.select`). Controlled: pass `value` + `onChange(value)`.
 *
 * Accessibility: WAI-ARIA listbox pattern with focus kept on the trigger and the
 * active option tracked via `aria-activedescendant`. Keyboard: Up/Down/Home/End
 * to move, Enter/Space to select, Esc to close, Tab closes. Click-outside closes.
 * A hidden input mirrors the value so the control still posts inside plain forms.
 */

export type SelectOption = { value: string; label: string; disabled?: boolean }

type Props = {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  /** Name for the mirrored hidden input (only needed for native form posts). */
  name?: string
  id?: string
  disabled?: boolean
  /** Applied to the root wrapper — use for width, e.g. "w-44" or "flex-1". */
  className?: string
  ariaLabel?: string
  /**
   * "bordered" (default) = the toolbar look (white surface, radius 11).
   * "field" = the modal/form look (filled surface-2, radius 9) — mockup `.field`.
   */
  variant?: 'bordered' | 'field'
}

export default function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  name,
  id,
  disabled = false,
  className = '',
  ariaLabel,
  variant = 'bordered',
}: Props) {
  const box =
    variant === 'field'
      ? 'rounded-[9px] bg-surface-2 px-[11px] py-[9px]'
      : 'rounded-[11px] bg-surface px-3 py-2'
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const baseId = useId()
  const listId = `${baseId}-list`
  const optId = (i: number) => `${baseId}-opt-${i}`

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined
  const firstEnabled = () => options.findIndex((o) => !o.disabled)

  // Outside-click is handled by Popover (the menu is portaled out of the root).

  // Keep the active option scrolled into view while navigating. (scrollIntoView
  // is absent under jsdom, hence the optional call.)
  useEffect(() => {
    if (open && activeIndex >= 0) {
      document
        .getElementById(`${baseId}-opt-${activeIndex}`)
        ?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [open, activeIndex, baseId])

  function openMenu() {
    if (disabled) return
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabled())
    setOpen(true)
  }

  function commit(i: number) {
    const opt = options[i]
    if (!opt || opt.disabled) return
    onChange(opt.value)
    setOpen(false)
  }

  function move(delta: number) {
    setActiveIndex((cur) => {
      let i = cur < 0 ? (delta > 0 ? -1 : options.length) : cur
      for (let step = 0; step < options.length; step++) {
        i = (i + delta + options.length) % options.length
        if (!options[i]?.disabled) return i
      }
      return cur
    })
  }

  function edge(fromEnd: boolean) {
    if (fromEnd) {
      for (let i = options.length - 1; i >= 0; i--)
        if (!options[i].disabled) return setActiveIndex(i)
    } else {
      setActiveIndex(firstEnabled())
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openMenu()
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        move(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        move(-1)
        break
      case 'Home':
        e.preventDefault()
        edge(false)
        break
      case 'End':
        e.preventDefault()
        edge(true)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(activeIndex)
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {name ? <input type="hidden" name={name} value={value} /> : null}
      <button
        ref={btnRef}
        type="button"
        role="combobox"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? optId(activeIndex) : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        className={`flex w-full items-center gap-2 border border-border text-left text-sm font-medium text-ink outline-none hover:border-border-2 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-60 aria-expanded:border-accent ${box}`}
      >
        <span className={`flex-1 truncate ${selected ? '' : 'text-ink-muted'}`}>
          {selected?.label ?? placeholder}
        </span>
        <Icon
          name="chevron-down"
          size={15}
          className={`flex-none text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <Popover anchorRef={btnRef} open={open} onClose={() => setOpen(false)} matchWidth>
        <div
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="max-h-72 overflow-auto rounded-xl border border-border bg-surface py-1 shadow-[var(--shadow)]"
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isActive = i === activeIndex
            return (
              // biome-ignore lint/a11y/useFocusableInteractive: listbox uses aria-activedescendant on the combobox trigger; options are intentionally not tab-focusable.
              <div
                key={opt.value}
                id={optId(i)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                onMouseEnter={() => !opt.disabled && setActiveIndex(i)}
                onMouseDown={(e) => {
                  // Keep focus on the trigger; select on pointer down.
                  e.preventDefault()
                  commit(i)
                }}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                  opt.disabled
                    ? 'cursor-not-allowed text-ink-muted'
                    : isActive
                      ? 'bg-surface-2 text-ink'
                      : 'text-ink'
                } ${isSelected ? 'font-semibold text-accent' : ''}`}
              >
                <span className="flex-1 truncate">{opt.label}</span>
                {isSelected ? (
                  <Icon name="check" size={15} className="flex-none text-accent" />
                ) : null}
              </div>
            )
          })}
        </div>
      </Popover>
    </div>
  )
}
